import { useCallback, useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useAppThunkDispatch } from 'state/store';
import {
  fetchProjects,
  selectAllProjects,
  selectTotalProjectsCount,
  selectLoadedProjectsCount,
  FetchProjectsThunkParams,
  selectProjectsListStatus,
  toggleSidePanelFilters,
  selectCanFetchMoreProjects,
  setFetchingMore,
  resetState,
} from 'state/ProjectsList/projectsListSlice';
import {
  selectSearchValue,
  setSearchValue,
  setSortValue,
  resetFilters,
} from 'state/ProjectsList/filters/filtersSlice';
import { fetchFilterSettingByType } from 'state/Settings/Filters/FiltersSlice';
import { resetSelectedProjects } from 'state/ProjectsList/export/exportSlice';
import { Project } from 'types/store/projectsList';
import { SETTINGS_FILTERS, SLICE_STATUS } from 'utils/constants';

export interface Response {
  projects: Project[];
  totalProjectsCount: number;
  loadedProjectsCount: number;
  init: () => Promise<void>;
  handleSort: (column: string) => void;
  isLoading: boolean;
  toggleSidePanelFilters: () => void;
  canFetchMoreProjects: boolean;
  fetchMoreProjects: () => void;
  searchProjects: (search: string) => void;
  searchValue?: string;
  handleResetState: () => void;
  fetchProjectsList: (params: FetchProjectsThunkParams) => Promise<any>;
  refreshProjectsList: () => Promise<void>;
}

const useConsolidatedProjectsList: () => Response = () => {
  const dispatch = useAppThunkDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const projects = useSelector(selectAllProjects);
  const totalProjectsCount = useSelector(selectTotalProjectsCount);
  const loadedProjectsCount = useSelector(selectLoadedProjectsCount);
  const projectsListStatus = useSelector(selectProjectsListStatus);
  const canFetchMoreProjects = useSelector(selectCanFetchMoreProjects);
  const searchValue = useSelector(selectSearchValue);

  const fetchProjectsList = useCallback(
    async (params: FetchProjectsThunkParams) => {
      return await dispatch(
        fetchProjects({ ...params, isConsolidatedView: true })
      );
    },
    [dispatch]
  );

  const init = useCallback(async () => {
    await dispatch(
      fetchFilterSettingByType(SETTINGS_FILTERS.CONSOLIDATED_PROJECTS_TABLE)
    );
    dispatch(setSortValue('-updatedAt'));
    await fetchProjectsList({
      includeTotalCount: true,
    });
  }, [dispatch, fetchProjectsList]);

  const handleSort = useCallback(
    (column: string) => {
      dispatch(setSortValue(column));
      fetchProjectsList({});
    },
    [dispatch, fetchProjectsList]
  );

  const handleToggleSidePanelFilters = useCallback(
    () => dispatch(toggleSidePanelFilters()),
    [dispatch]
  );

  const fetchMoreProjects = useCallback(() => {
    dispatch(setFetchingMore(true));
    fetchProjectsList({});
  }, [dispatch, fetchProjectsList]);

  const searchProjects = useCallback(
    (search: string) => {
      dispatch(setSearchValue(search));
      fetchProjectsList({
        includeTotalCount: true,
      });
    },
    [dispatch, fetchProjectsList]
  );
  const handleResetState = useCallback(() => {
    dispatch(resetState());
    dispatch(resetFilters());
    dispatch(resetSelectedProjects());
  }, [dispatch]);

  const refreshProjectsList = useCallback(async () => {
    setIsLoading(true);
    await fetchProjectsList({
      includeTotalCount: true,
    });
  }, [fetchProjectsList]);

  useEffect(() => {
    if (
      projectsListStatus &&
      projectsListStatus !== SLICE_STATUS.LOADING &&
      isLoading
    ) {
      setIsLoading(false);
    }
  }, [projectsListStatus, isLoading]);

  return useMemo(
    () => ({
      init,
      projects,
      totalProjectsCount,
      loadedProjectsCount,
      handleSort,
      isLoading,
      toggleSidePanelFilters: handleToggleSidePanelFilters,
      canFetchMoreProjects,
      fetchMoreProjects,
      searchProjects,
      searchValue,
      handleResetState,
      fetchProjectsList,
      refreshProjectsList,
    }),
    [
      init,
      projects,
      totalProjectsCount,
      loadedProjectsCount,
      handleSort,
      isLoading,
      handleToggleSidePanelFilters,
      canFetchMoreProjects,
      fetchMoreProjects,
      searchProjects,
      searchValue,
      handleResetState,
      fetchProjectsList,
      refreshProjectsList,
    ]
  );
};

export default useConsolidatedProjectsList;
